import React from 'react';
import {observer} from 'mobx-react';
import {TechseeBaseComponent} from '@techsee/techsee-ui-common/lib/_shared/techsee-base-component';
import {GenericModal} from '@techsee/techsee-ui-common/lib/modals/generic';
import Translate from '../../components/translate/Translate';
import {SmsStatusModalController} from './SmsStatusModal.controller';
import {requireImage} from '../../utils/utils';
import './SmsStatusModal.scss';

interface SmsStatusModalProps {
    ctrl: SmsStatusModalController;
}

const Header = observer(({ctrl}: {ctrl: SmsStatusModalController}) => {
    if (ctrl.useNewInterface) {
        return (
            <div className='on-video-alert-header' dir={ctrl.dir}>
                {ctrl.state.title ? (
                    <h3 className='modal-title'>
                        <Translate text={ctrl.state.title} />
                    </h3>
                ) : (
                    <img src={requireImage('attention.png')} alt='Attention' />
                )}
            </div>
        );
    } else {
        // Old UI: Standard header
        return (
            <div className='modal-header' dir={ctrl.dir}>
                <h3 className='modal-title'>
                    <Translate text='SMS_STATUS.VIEW.TITLE' />
                </h3>
            </div>
        );
    }
});

const Content = observer(({ctrl}: {ctrl: SmsStatusModalController}) => {
    if (ctrl.useNewInterface) {
        // New UI: Message content
        return (
            <div className='on-video-alert-message' dir={ctrl.dir}>
                <p>
                    <Translate text={ctrl.state.message} />
                </p>
            </div>
        );
    } else {
        // Old UI: Show message or question
        return (
            <div className='modal-body' dir={ctrl.dir}>
                {!ctrl.state.question && ctrl.state.message && <p>{ctrl.state.message}</p>}
                {ctrl.state.question && (
                    <p>
                        <Translate text='SMS_STATUS.VIEW.QUESTION' />
                    </p>
                )}
            </div>
        );
    }
});

const Footer = observer(({ctrl}: {ctrl: SmsStatusModalController}) => {
    if (ctrl.useNewInterface) {
        // New UI: Custom styled buttons
        return (
            <div className='on-video-alert-footer' dir={ctrl.dir}>
                <button className='recieved-sms' onClick={() => ctrl.answer(true)}>
                    <span>
                        <Translate text='SMS_STATUS.VIEW.YES' />
                    </span>
                </button>
                <button onClick={() => ctrl.answer(false)}>
                    <span>
                        <Translate text='SMS_STATUS.VIEW.NO' />
                    </span>
                </button>
            </div>
        );
    } else {
        // Old UI: Bootstrap buttons
        return (
            <div className='modal-footer' dir={ctrl.dir}>
                {!ctrl.state.question && (
                    <button className='btn btn-primary' type='button' onClick={() => ctrl.ok()}>
                        <Translate text='SMS_STATUS.VIEW.OK' />
                    </button>
                )}
                {ctrl.state.question && (
                    <>
                        <button className='btn btn-primary' type='button' onClick={() => ctrl.answer(true)}>
                            <Translate text='SMS_STATUS.VIEW.YES' />
                        </button>
                        <button className='btn btn-primary' type='button' onClick={() => ctrl.answer(false)}>
                            <Translate text='SMS_STATUS.VIEW.NO' />
                        </button>
                    </>
                )}
            </div>
        );
    }
});

@observer
export class SmsStatusModal extends TechseeBaseComponent<SmsStatusModalProps> {
    renderInternal(): JSX.Element {
        const {ctrl} = this.props;

        // Extract values once to avoid multiple property accesses
        const useNewInterface = ctrl.useNewInterface;
        const isMobile = ctrl.isMobile;
        const isVisible = ctrl.state.isVisible;

        const className = useNewInterface
            ? 'modal-dialog on-new-ui-sms-status' + (isMobile ? ' mobile-modal' : '')
            : 'modal-dialog sms-status-modal';

        return (
            <GenericModal
                className={className}
                header={() => <Header ctrl={ctrl} />}
                content={() => <Content ctrl={ctrl} />}
                toolbar={() => <Footer ctrl={ctrl} />}
                show={isVisible}
                onHideHandler={() => ctrl.dismiss()}
                closeOnEscape={!useNewInterface}
                isCloseVisible={false}
            />
        );
    }
}
